/**
 * Maps Taste Trends Chatbot schema v1.0 widgets + datasets to render-ready shapes.
 */

export function datasetById(datasets, id) {
  if (!Array.isArray(datasets) || id == null) return null;
  return datasets.find((item) => String(item.id) === String(id)) || null;
}

function sortRowsByXField(rows, xField, xType) {
  if (!Array.isArray(rows) || rows.length < 2) return rows || [];

  if (xType === "ordinal" && rows.every((row) => /^\d{4}-Q[1-4]$/.test(row[xField]))) {
    return [...rows].sort((a, b) => String(a[xField]).localeCompare(String(b[xField])));
  }

  return rows;
}

/**
 * Converts a v1 bar/line widget into the visualization shape used by Chatbot charts.
 */
function encodingChannel(channel) {
  if (!channel) return null;
  if (typeof channel === "string") return { field: channel };
  const field = channel.field || channel.key || channel.column;
  if (!field) return null;
  return { ...channel, field };
}

const QUARTER_RE = /^\d{4}-Q[1-4]$/;

function isQuarterValue(value) {
  return typeof value === "string" && QUARTER_RE.test(value);
}

function sortedQuarterNames(values) {
  return [...new Set(values.filter(isQuarterValue))].sort((a, b) =>
    String(a).localeCompare(String(b))
  );
}

function columnKeys(dataset) {
  return (dataset.columns || []).map((column) => column.key).filter(Boolean);
}

function quarterColumnKey(dataset, rows) {
  const keys = columnKeys(dataset);
  const named = ["quarter", "period", "periods"].find(
    (key) => keys.includes(key) || rows.some((row) => row[key] != null)
  );
  if (named && rows.some((row) => isQuarterValue(row[named]))) return named;

  return keys.find((key) => rows.some((row) => isQuarterValue(row[key]))) || null;
}

function uniqueCategories(rows, field) {
  return [...new Set(rows.map((row) => row[field]).filter((value) => value != null))];
}

function chartFromSeries({ widget, categories, series, yAxisName }) {
  return {
    type: widget.type,
    title: widget.title,
    xAxis: { type: "category", data: categories },
    yAxis: { type: "value", name: yAxisName },
    series,
  };
}

function seriesByKey(rows, xField, seriesKey, yField) {
  const categories = uniqueCategories(rows, xField);
  const names = [...new Set(rows.map((row) => row[seriesKey]).filter((value) => value != null))];
  const orderedNames = sortedQuarterNames(names);
  const seriesNames = orderedNames.length === names.length ? orderedNames : names;

  return chartFromSeries({
    widget: { type: rows.__widgetType, title: rows.__widgetTitle },
    categories,
    yAxisName: rows.__yAxisName,
    series: seriesNames.map((name) => ({
      name: String(name),
      data: categories.map((category) => {
        const match = rows.find(
          (row) => row[xField] === category && row[seriesKey] === name
        );
        const value = match?.[yField];
        return value === undefined ? null : value;
      }),
    })),
  });
}

function previousValueKey(yField, dataset) {
  const keys = columnKeys(dataset);
  return [
    `previous_${yField}`,
    `${yField}_previous`,
    `prev_${yField}`,
    "previous_value",
    "previous",
    "comparison_value",
  ].find((key) => keys.includes(key) || (dataset.rows || []).some((row) => row[key] != null));
}

function comparisonChart(widget, dataset, rows, x, y, yAxisName, context) {
  const periodKey = quarterColumnKey(dataset, rows);
  const quartersInRows = periodKey
    ? sortedQuarterNames(rows.map((row) => row[periodKey]))
    : [];

  if (periodKey && quartersInRows.length > 1 && x.field !== periodKey) {
    const categories = uniqueCategories(rows, x.field);
    return chartFromSeries({
      widget,
      categories,
      yAxisName,
      series: quartersInRows.map((quarter) => ({
        name: quarter,
        data: categories.map((category) => {
          const match = rows.find(
            (row) => row[x.field] === category && row[periodKey] === quarter
          );
          const value = match?.[y.field];
          return value === undefined ? null : value;
        }),
      })),
    });
  }

  const declaredPeriods = sortedQuarterNames(context?.periods || []);
  const widePeriodKeys = [
    ...new Set([
      ...columnKeys(dataset).filter(isQuarterValue),
      ...declaredPeriods.filter((period) =>
        rows.some((row) => row[period] != null)
      ),
    ]),
  ].sort((a, b) => a.localeCompare(b));

  if (widePeriodKeys.length > 1 && !widePeriodKeys.includes(x.field)) {
    const categories = uniqueCategories(rows, x.field);
    return chartFromSeries({
      widget,
      categories,
      yAxisName,
      series: widePeriodKeys.map((period) => ({
        name: period,
        data: categories.map((category) => {
          const match = rows.find((row) => row[x.field] === category);
          const value = match?.[period];
          return value === undefined ? null : value;
        }),
      })),
    });
  }

  const previousKey = previousValueKey(y.field, dataset);
  if (!previousKey || previousKey === y.field) return null;

  const earlier = declaredPeriods[0] || "Previous";
  const later = declaredPeriods[declaredPeriods.length - 1] || "Current";
  const categories = uniqueCategories(rows, x.field);

  return chartFromSeries({
    widget,
    categories,
    yAxisName,
    series: [
      {
        name: earlier,
        data: categories.map((category) => {
          const match = rows.find((row) => row[x.field] === category);
          const value = match?.[previousKey];
          return value === undefined ? null : value;
        }),
      },
      {
        name: later === earlier ? "Current" : later,
        data: categories.map((category) => {
          const match = rows.find((row) => row[x.field] === category);
          const value = match?.[y.field];
          return value === undefined ? null : value;
        }),
      },
    ],
  });
}

export function visualizationFromV1Widget(widget, dataset, context = null) {
  if (!widget || !dataset || !widget.encoding) return null;

  const x = encodingChannel(widget.encoding.x);
  const y = encodingChannel(widget.encoding.y);
  const seriesField = encodingChannel(widget.encoding.series);
  if (!x?.field || !y?.field) return null;

  const rows = sortRowsByXField(dataset.rows || [], x.field, x.type);
  if (!rows.length) return null;

  const yAxisName =
    y.unit === "percent" || y.unit === "percentage_points"
      ? y.unit === "percentage_points"
        ? "Percentage points"
        : "%"
      : y.unit || "";

  if (seriesField?.field) {
    const built = seriesByKey(rows, x.field, seriesField.field, y.field);
    return {
      ...built,
      type: widget.type,
      title: widget.title,
      yAxis: { type: "value", name: yAxisName },
    };
  }

  const compared = comparisonChart(widget, dataset, rows, x, y, yAxisName, context);
  if (compared) return compared;

  if (rows.some((row) => row.value_a != null || row.value_b != null)) {
    return null;
  }

  const labels = rows.map((row) => row[x.field]);
  const values = rows.map((row) => {
    const value = row[y.field];
    return value === undefined ? null : value;
  });

  return {
    type: widget.type,
    title: widget.title,
    xAxis: { type: "category", data: labels },
    yAxis: { type: "value", name: yAxisName },
    series: [{ name: widget.title || y.field, data: values }],
  };
}

const COMPARISON_VALUE_FIELDS = {
  value_a: { index: 0, periodField: "period_a" },
  value_b: { index: 1, periodField: "period_b" },
};

function formatLabel(value) {
  if (value == null) return "";
  return String(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function displayPeriod(value) {
  const text = String(value).trim();
  const canonical = text.match(/^(\d{4})-Q([1-4])$/i);
  if (canonical) return `Q${canonical[2]} ${canonical[1]}`;
  const spoken = text.match(/^Q([1-4])[\s-]+(\d{4})$/i);
  if (spoken) return `Q${spoken[1]} ${spoken[2]}`;
  return text;
}

function formatDifference(difference) {
  const formatted = new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(Math.abs(difference));
  if (difference > 0) return `Up ${formatted}`;
  if (difference < 0) return `Down ${formatted}`;
  return "No change";
}

/**
 * One horizontal comparison chart per metric, with the two quarters as the bars.
 */
export function perMetricComparisonCharts(widget, dataset, context = null) {
  const rows = dataset?.rows || [];
  if (!rows.some((row) => row?.value_a != null || row?.value_b != null)) {
    return [];
  }

  const periodA = periodLabelForValueColumn("value_a", dataset, context);
  const periodB = periodLabelForValueColumn("value_b", dataset, context);
  if (!periodA || !periodB) return [];

  const xChannel = encodingChannel(widget?.encoding?.x);
  const metricField = xChannel?.field || "metric";

  return rows
    .filter((row) => row?.[metricField] != null)
    .map((row) => {
      const title = formatLabel(row[metricField]);
      const difference =
        typeof row.difference === "number" ? row.difference : null;

      return {
        type: "bar",
        layout: "compare",
        title,
        subtitle: difference == null ? null : formatDifference(difference),
        xAxis: { type: "category", data: [periodA, periodB] },
        yAxis: { type: "value" },
        series: [
          {
            name: title,
            data: [
              row.value_a === undefined ? null : row.value_a,
              row.value_b === undefined ? null : row.value_b,
            ],
          },
        ],
      };
    })
    .filter((chart) => chart.series[0].data.some((value) => value != null));
}

function periodLabelForValueColumn(key, dataset, context) {
  const mapping = COMPARISON_VALUE_FIELDS[key];
  if (!mapping) return null;

  const labels = [
    ...new Set(
      (dataset.rows || [])
        .map((row) => row?.[mapping.periodField])
        .filter((value) => value != null && String(value).trim())
        .map((value) => String(value))
    ),
  ];
  if (labels.length) {
    return labels.length === 1 ? displayPeriod(labels[0]) : null;
  }

  const fromContext = context?.periods?.[mapping.index];
  return fromContext != null && String(fromContext).trim()
    ? displayPeriod(fromContext)
    : null;
}

export function tableFromV1Widget(widget, dataset, context = null) {
  if (!widget || !dataset || widget.type !== "table") return null;

  const fields = Array.isArray(widget.fields) ? widget.fields : [];
  if (!fields.length) return null;

  const columns = fields.map((key) => {
    const col = (dataset.columns || []).find((c) => c.key === key);
    const periodLabel = periodLabelForValueColumn(key, dataset, context);
    return {
      key,
      type: col?.type || "string",
      label: periodLabel || col?.label || null,
    };
  });

  return {
    title: widget.title,
    columns,
    rows: (dataset.rows || []).map((row) =>
      fields.map((key) => {
        const value = row[key] === undefined ? null : row[key];
        if (key === "metric" && typeof value === "string") {
          return formatLabel(value);
        }
        return value;
      })
    ),
  };
}

export function kpiFromV1Widget(widget, dataset) {
  if (!widget || !dataset || widget.type !== "kpi") return null;

  const row = (dataset.rows || [])[0];
  if (!row) return null;

  const value = row[widget.value_field];
  if (value === undefined) return null;

  const unit = widget.unit || "";
  let label = widget.title || widget.value_field;

  const kpi = {
    label,
    value,
    unit,
  };

  if (widget.comparison_field && row[widget.comparison_field] !== undefined) {
    kpi.change = row[widget.comparison_field];
  }

  return kpi;
}

export function isV1Response(payload) {
  const body = unwrapChatPayload(payload);
  if (!body || typeof body !== "object" || body.uiNormalized) return false;
  return (
    body.schema_version === "1.0" ||
    Array.isArray(body.widgets) ||
    Array.isArray(body.datasets) ||
    (body.answer != null && typeof body.answer === "object")
  );
}

export function isNormalizedChatPayload(payload) {
  return Boolean(payload && typeof payload === "object" && payload.uiNormalized === true);
}

function tryParseJson(value) {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

function looksLikeChatBody(value) {
  return Boolean(
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    (value.schema_version ||
      value.answer ||
      value.reply ||
      value.widgets ||
      value.datasets ||
      value.visualization ||
      value.clarification)
  );
}

export function unwrapChatPayload(payload) {
  let current = tryParseJson(payload);
  if (!current || typeof current !== "object" || Array.isArray(current)) {
    return current;
  }

  const nestedKeys = ["data", "result", "response", "payload", "body"];
  for (const key of nestedKeys) {
    const nested = tryParseJson(current[key]);
    if (!looksLikeChatBody(nested)) continue;
    const outerIsChat =
      current.schema_version ||
      current.answer ||
      current.widgets ||
      current.datasets ||
      current.visualization;
    if (!outerIsChat) {
      return unwrapChatPayload(nested);
    }
  }

  return current;
}

function asText(value) {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
}

export function extractAnswerText(result) {
  if (result == null) return "";
  if (typeof result === "string") return result.trim();

  const answer = result.answer;
  const fromAnswer =
    asText(answer) ||
    asText(answer?.text) ||
    asText(answer?.message) ||
    asText(answer?.content) ||
    asText(answer?.reply);

  return (
    fromAnswer ||
    asText(result.reply) ||
    asText(result.message) ||
    asText(result.text) ||
    asText(result.clarification?.question) ||
    asText(result.data?.answer?.text) ||
    asText(result.data?.reply) ||
    ""
  );
}

function mapStatus(status) {
  if (status === "ok" || status === "success") return "success";
  if (!status) return "success";
  return status;
}

function legacyChart(source, title) {
  if (!source || typeof source !== "object") return null;
  const viz = source.visualization && source.visualization !== source
    ? source.visualization
    : source;
  const type = viz.type;
  if (!["bar", "line", "pie"].includes(type)) return null;
  if (!Array.isArray(viz.series) || viz.series.length === 0) return null;
  return {
    ...viz,
    title: viz.title || title || source.title || "",
  };
}

/**
 * Normalizes schema v1.0 and legacy chat API payloads into one UI shape.
 */
export function normalizeChatPayload(input) {
  if (isNormalizedChatPayload(input)) {
    return input;
  }

  if (input == null || input === "") {
    return {
      uiNormalized: true,
      status: "success",
      intent: "unknown",
      reply: "I couldn't generate an answer.",
      kpis: [],
      visualizations: [],
      tables: [],
      data: {},
      insights: [],
      follow_up_questions: [],
      sources: [],
      clarification: null,
      conversation: null,
      provenance: null,
      context: null,
      schema_version: null,
      visualization: {
        type: "none",
        title: null,
        x_axis: null,
        y_axis: null,
        series: [],
      },
    };
  }

  if (typeof input === "string") {
    const parsed = tryParseJson(input);
    if (parsed && typeof parsed === "object") {
      return normalizeChatPayload(parsed);
    }
    return {
      uiNormalized: true,
      status: "success",
      intent: "local",
      reply: input,
      kpis: [],
      visualizations: [],
      tables: [],
      data: {},
      insights: [],
      follow_up_questions: [],
      sources: [],
      clarification: null,
      conversation: null,
      provenance: null,
      context: null,
      schema_version: null,
      visualization: {
        type: "none",
        title: null,
        series: [],
      },
    };
  }

  const result = unwrapChatPayload(input) || input;
  const datasets = Array.isArray(result.datasets)
    ? result.datasets
    : Array.isArray(result.data?.datasets)
      ? result.data.datasets
      : [];
  const widgets = Array.isArray(result.widgets)
    ? result.widgets
    : Array.isArray(result.data?.widgets)
      ? result.data.widgets
      : [];

  const visualizations = [];
  const tables = [];
  const kpis = Array.isArray(result.kpis) ? [...result.kpis] : [];

  widgets.forEach((widget) => {
    const dataset = datasetById(
      datasets,
      widget.dataset_id || widget.datasetId
    );
    const widgetType = String(widget.type || "").toLowerCase();

    if (dataset && (widgetType === "bar" || widgetType === "line")) {
      const comparisons = perMetricComparisonCharts(
        { ...widget, type: widgetType },
        dataset,
        result.context
      );
      if (comparisons.length) {
        visualizations.push(...comparisons);
        return;
      }

      const viz = visualizationFromV1Widget(
        { ...widget, type: widgetType },
        dataset,
        result.context
      );
      if (viz) {
        visualizations.push(viz);
        return;
      }
    }

    if (dataset && widgetType === "table") {
      const table = tableFromV1Widget(
        { ...widget, type: "table" },
        dataset,
        result.context
      );
      if (table) {
        tables.push(table);
        return;
      }
    }

    if (dataset && widgetType === "kpi") {
      const kpi = kpiFromV1Widget({ ...widget, type: "kpi" }, dataset);
      if (kpi) {
        kpis.push(kpi);
        return;
      }
    }

    const embedded = legacyChart(widget, widget.title);
    if (embedded) visualizations.push(embedded);
  });

  const embeddedResponseChart = legacyChart(result.visualization, result.visualization?.title);
  if (embeddedResponseChart) visualizations.push(embeddedResponseChart);

  const followUps = Array.isArray(result.clarification?.options)
    ? result.clarification.options
    : Array.isArray(result.follow_up_questions)
      ? result.follow_up_questions
      : [];

  const warnings = Array.isArray(result.provenance?.warnings)
    ? result.provenance.warnings
    : Array.isArray(result.insights)
      ? result.insights
      : [];

  const reply =
    extractAnswerText(result) ||
    (visualizations.length || tables.length || kpis.length
      ? ""
      : "I couldn't generate an answer.");

  return {
    uiNormalized: true,
    schema_version: result.schema_version || null,
    status: mapStatus(result.status),
    intent: result.intent || (result.schema_version ? "analytics" : "unknown"),
    reply,
    kpis,
    visualizations,
    tables,
    data: result.data && !result.data.datasets ? result.data : { context: result.context, datasets },
    insights: warnings,
    follow_up_questions: followUps,
    sources: result.sources || (result.provenance?.source_id
      ? [{ id: result.provenance.source_id, query_id: result.provenance.query_id }]
      : []),
    clarification: result.clarification || null,
    conversation: result.conversation || null,
    provenance: result.provenance || null,
    context: result.context || null,
    visualization: visualizations[0] || {
      type: "none",
      title: null,
      x_axis: null,
      y_axis: null,
      series: [],
    },
  };
}
